import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimatedNumber } from '@/components/AnimatedNumber';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { Box } from '@/components/lib/components';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useIsCashCurrency } from '@/hooks/use-social-currency';

import { betPayoutSelector } from '../utils/betslip-selectors';

export const PayoutInput = ({ betId }: { betId: string }) => {
    const { t } = useTranslation('betslip_sbk');
    const isCashCurrency = useIsCashCurrency();
    const payout = useSbkBetSlipStore(betPayoutSelector(betId, isCashCurrency));
    const isBetrBucks = useSbkBetSlipStore(state => state.isBetrBucksEnabled[betId]);
    const { formatCurrency } = useFormatCurrency();
    const hasSocialWallet = useHasSocialWallet();

    const prevIsBetrBucksRef = useRef(isBetrBucks);
    const shouldAnimate = prevIsBetrBucksRef.current !== isBetrBucks;
    prevIsBetrBucksRef.current = isBetrBucks;

    const displayFormat = useCallback(
        (value: number) => {
            if (!value) {
                return hasSocialWallet ? ' ' : '$';
            }
            return formatCurrency(value);
        },
        [formatCurrency, hasSocialWallet]
    );

    return (
        <Box alignItems="flex-start">
            <Text variant="bodySmall" letterSpacing={-0.08} color="neutral300">
                {t('payout')}
            </Text>
            <Row alignItems="center" gap="s4">
                <SocialCurrencyActiveIcon />
                <AnimatedNumber
                    value={payout}
                    variant="bodyLarge"
                    lineHeight={24}
                    letterSpacing={-0.43}
                    color="neutral300"
                    numberOfLines={1}
                    animate={shouldAnimate}
                    displayFormat={displayFormat}
                    alignText="flex-end"
                />
            </Row>
        </Box>
    );
};
